import { Component, ElementRef, EventEmitter, OnInit, Output, Renderer2 } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { getApp, getApps, initializeApp } from 'firebase/app';
import { getAuth, RecaptchaVerifier, getMultiFactorResolver, PhoneMultiFactorGenerator, PhoneAuthProvider, getAdditionalUserInfo } from 'firebase/auth';
import { environment } from 'src/environments/environment';
import { OtpComponent } from '../auth/components/otp/otp.component';
import { AuthService } from '../core/services/auth.service';
import { DataService } from '../core/services/data.service';
import { EncryptionService } from '../core/services/encryption.service';
import { EventService } from '../core/services/event.service';
import { AnalyticsService } from '../core/services/analytics.service';
import { ToastService } from '../core/services/toast.service';
import { isIOSStandalone } from '../utils/auth.utils';

@Component({
  selector: 'app-login-popup',
  templateUrl: './login-popup.component.html',
  styleUrls: ['./login-popup.component.css']
})
export class LoginPopupComponent implements OnInit {
  @Output() closePopup = new EventEmitter<void>();
  @Output() redirect = new EventEmitter<void>();

  currentPlatform: string | undefined;
  currentUrl: string | undefined;
  encryptedData: any;
  decryptedData: any;
  loginMode: 'web' | 'app' = 'web';
  isAndroid = false;
  isIOS = false;
  close() {
    this.analytics.capture('login_popup_dismissed', { surface: 'login_popup' });
    this.closePopup.emit();
  }

  onOverlayClick(event: MouseEvent) {
    // Only close if clicking directly on the overlay, not on child elements
    if ((event.target as HTMLElement).classList.contains('popup-overlay')) {
      this.close();
    }
  }
  constructor(private renderer: Renderer2, private el: ElementRef,
    private authService: AuthService,
    private router: Router, public dataService: DataService,
    private encryptionService: EncryptionService, private eventService: EventService,
    public dialog: MatDialog, public fbAuth: AngularFireAuth, private analytics: AnalyticsService,
    private toastService: ToastService) { }

  /**
   * Google/Apple sign-in popups can't complete in an iOS home-screen PWA —
   * see isIOSStandalone() for why. Heading it off here means the user gets an
   * actionable message instead of watching a popup show Firebase's own
   * "missing initial state" error and getting stuck.
   */
  private blockedByStandaloneIOS(): boolean {
    if (!isIOSStandalone()) return false;
    this.toastService.showToast(
      'Sign-in isn\'t supported in the installed app on iPhone/iPad yet. Please open picaggo.com in Safari to sign in.',
      6000
    );
    return true;
  }

  /**
   * Fallback for a Google/Apple sign-in failure that isn't MFA and wasn't
   * already explained above (e.g. blockedByStandaloneIOS()). Previously these
   * branches only logged to a (commented-out) console — the user was left on
   * the login screen with zero indication anything went wrong. Skips the
   * user-initiated cancel codes, which aren't failures worth surfacing.
   */
  private showGenericSignInError(err: any): void {
    if (err?.code === 'auth/popup-closed-by-user' || err?.code === 'auth/cancelled-popup-request') return;
    this.toastService.showToast('Sign-in didn\'t complete. Please try again.', 4000);
  }

  ngOnInit() {
    this.analytics.capture('login_popup_viewed', { surface: 'login_popup' });
    var userAgent = navigator.userAgent;
    if (userAgent.match(/iPad|iPhone|iPod/)) {
      // This is likely an iOS device
      // console.log("Apple"); //__CONSOLE_LOG__
      this.currentPlatform = 'Apple';
    } else if (userAgent.match(/Macintosh/) !== null) {
      // console.log("Apple"); //__CONSOLE_LOG__
      this.currentPlatform = 'Apple';
    } else {
      // Not an iOS device
      this.currentPlatform = 'Other';
      // console.log("Other"); //__CONSOLE_LOG__
    }
    // console.log('this.redirect', this.redirect); //__CONSOLE_LOG__
    //    const ua = navigator.userAgent.toLowerCase();
    // this.isAndroid = /android/.test(ua);
    // this.isIOS = /iphone|ipad|ipod/.test(ua);
  }

  async signUpWithGoogle() {
    this.analytics.capture('login_provider_clicked', { provider: 'google', surface: 'login_popup' });
    if (this.blockedByStandaloneIOS()) return;
    localStorage.removeItem('gallery_branding');
    this.closePopup.emit();
    this.authService.signUpWithGoogle()
      .then(async (res: any) => {
        // console.log('Google sign-up success:', res); //__CONSOLE_LOG__

        // Encrypt and store user locally
        this.encryptedData = this.encryptionService.encryptData(JSON.stringify(res.user));
        localStorage.setItem('user', this.encryptedData);
        this.decryptedData = this.encryptionService.decryptData(this.encryptedData);
        // console.log('User data encrypted and stored:', this.decryptedData); //__CONSOLE_LOG__

        // Use existing Firebase app or initialize
        const app = getApps().length ? getApp() : initializeApp(environment.firebase);
        // console.log('Firebase app instance:', app); //__CONSOLE_LOG__

        const auth = getAuth(app);
        // console.log('Firebase auth instance:', auth); //__CONSOLE_LOG__

        // Ensure recaptcha container exists
        const recaptchaContainer = document.getElementById('recaptcha-container');
        if (!recaptchaContainer) {
          // console.error('recaptcha-container element not found!'); //__CONSOLE_LOG__
          return;
        }

        // Setup invisible reCAPTCHA
        const recaptchaVerifier = new RecaptchaVerifier(
          'recaptcha-container',
          {
            size: 'invisible',
            callback: (response: any) => {

            },
            'expired-callback': () => {
              // console.log('reCAPTCHA expired'); //__CONSOLE_LOG__
            }
          },
          auth
        );

        // Call backend signIn (no MFA)
        const user = res.user._delegate || res.user;
        const isNewUser = res.additionalUserInfo?.isNewUser ?? false;
        (await this.authService.signIn(user, null, isNewUser)).subscribe({
          error: () => {
            this.analytics.capture('login_failed', { provider: 'google', mfa: false, reason: 'backend_signin_failed', surface: 'login_popup' });
          },
          next: async (data: any) => {
          // console.log('Backend signIn response (no MFA) //__CONSOLE_LOG__:', data);
          this.analytics.capture('login_succeeded', { provider: 'google', mfa: false, is_new_user: isNewUser, surface: 'login_popup' });
          this.dataService.showTopBar = true;

          if (localStorage.getItem('eventId')) {
            this.confirmRSVP();
          } else {
            this.router.navigateByUrl("/home");
          }
          }
        });
      })
      .catch(async (err) => {

        if (err.code === "auth/multi-factor-auth-required") {
          // console.log('MFA required:', err); //__CONSOLE_LOG__
          this.analytics.capture('login_mfa_required', { provider: 'google', surface: 'login_popup' });

          const app = getApps().length ? getApp() : initializeApp(environment.firebase);
          const auth = getAuth(app);

          const recaptchaContainer = document.getElementById('recaptcha-container');
          if (!recaptchaContainer) {
            // console.error('recaptcha-container element not found in MFA!'); //__CONSOLE_LOG__
            return;
          }

          const recaptchaVerifier = new RecaptchaVerifier(
            'recaptcha-container',
            {
              size: 'invisible',
              callback: (response: any) => console.log('reCAPTCHA solved (MFA):', response),
              'expired-callback': () => console.log('reCAPTCHA expired (MFA)')
            },
            auth
          );
          // console.log('RecaptchaVerifier instance (MFA) //__CONSOLE_LOG__:', recaptchaVerifier);

          // Get MFA resolver
          const resolver: any = getMultiFactorResolver(auth, err);
          // console.log('MFA resolver:', resolver); //__CONSOLE_LOG__

          // Only handle phone factor
          const hint = resolver.hints[0];
          if (hint.factorId === PhoneMultiFactorGenerator.FACTOR_ID) {
            // console.log('Phone MFA selected'); //__CONSOLE_LOG__
            const phoneInfoOptions = { multiFactorHint: hint, session: resolver.session };
            // console.log('Phone info options:', phoneInfoOptions); //__CONSOLE_LOG__

            const phoneAuthProvider = new PhoneAuthProvider(auth);
            localStorage.setItem('pNo', phoneInfoOptions.multiFactorHint.phoneNumber);

            try {
              const verificationId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier);
              // console.log('SMS verificationId:', verificationId); //__CONSOLE_LOG__

              const dialogRef = this.dialog.open(OtpComponent, {
                maxWidth: '100vw',
                maxHeight: '100vh',
                height: '100%',
                width: '100%'
              });

              dialogRef.afterOpened().subscribe(() => {
                // console.log('OTP dialog opened'); //__CONSOLE_LOG__
                const dialogContainer = this.renderer.selectRootElement('.fade-background');
                this.renderer.setStyle(dialogContainer, 'z-index', '1');
              });

              dialogRef.afterClosed().subscribe(async (verificationCode) => {
                // console.log('OTP dialog closed with code:', verificationCode); //__CONSOLE_LOG__
                if (!verificationCode) {
                  this.analytics.capture('login_otp_dismissed', { provider: 'google', surface: 'login_popup' });
                  return;
                }
                // Create MFA assertion
                const cred = PhoneAuthProvider.credential(verificationId, verificationCode);
                const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);

                let userCredential;
                try {
                  userCredential = await resolver.resolveSignIn(multiFactorAssertion);
                } catch (otpErr) {
                  this.analytics.capture('login_failed', { provider: 'google', mfa: true, reason: 'invalid_otp', surface: 'login_popup' });
                  return;
                }
                const user = userCredential.user;
                // console.log('User signed in with MFA:', user); //__CONSOLE_LOG__

                if (!user.isAnonymous) {
                  // Refresh user info
                  await user.reload();
                  const phoneInfo = localStorage.getItem('pNo');// || user.multiFactor.enrolledFactors[0]?.phoneNumber;
                  (await
                    // Call backend signIn
                    this.authService.signIn(user, phoneInfo)).subscribe({
                      error: () => {
                        this.analytics.capture('login_failed', { provider: 'google', mfa: true, reason: 'backend_signin_failed', surface: 'login_popup' });
                      },
                      next: async (data: any) => {
                      this.analytics.capture('login_succeeded', { provider: 'google', mfa: true, surface: 'login_popup' });
                      this.dataService.showTopBar = true;

                      if (localStorage.getItem('eventId')) {
                        this.confirmRSVP();
                      } else {
                        this.router.navigateByUrl("/home");
                      }
                      }
                    });
                }
              });

            } catch (phoneErr) {
              // console.error('Error verifying phone number:', phoneErr); //__CONSOLE_LOG__
            }
          } else {
            // console.warn('Unsupported MFA factor:', hint.factorId); //__CONSOLE_LOG__
          }
        } else {
          // console.error('Unhandled Firebase error:', err); //__CONSOLE_LOG__
          const cancelled = err?.code === 'auth/popup-closed-by-user' || err?.code === 'auth/cancelled-popup-request';
          this.analytics.capture(cancelled ? 'login_dismissed' : 'login_failed', {
            provider: 'google', surface: 'login_popup',
            ...(cancelled ? {} : { code: err?.code, reason: 'provider_error' }),
          });
          this.showGenericSignInError(err);
        }
      });
  }


  async signUpWithGoogle1() {
    this.closePopup.emit();
    await this.authService
      .signUpWithGoogle()
      .then((res: any) => {
        this.encryptedData = this.encryptionService.encryptData(JSON.stringify(res.user));
        localStorage.setItem('user', this.encryptedData);
        this.decryptedData = this.encryptionService.decryptData(this.encryptedData);
        const app = initializeApp(environment.firebase);
        const auth = getAuth(app);
        const recaptchaVerifier = new RecaptchaVerifier(
          "recaptcha-container",
          {
            'size': 'invisible',
            'callback': (response: any) => {
              // reCAPTCHA solved, allow signInWithPhoneNumber.
            }
          },
          auth
        );


      })
      .catch((err) => {
        // console.error(err); //__CONSOLE_LOG__
        if (err.code == "auth/multi-factor-auth-required") {
          const app = initializeApp(environment.firebase);
          const auth = getAuth(app);
          const recaptchaVerifier = new RecaptchaVerifier(
            "recaptcha-container",
            {
              'size': 'invisible',
              'callback': (response: any) => {
                // reCAPTCHA solved, allow signInWithPhoneNumber.
                // console.log('callback recaptcha') //__CONSOLE_LOG__
              },
              'expired-callback': () => {

              }
            },
            auth
          );
          const resolver: any = getMultiFactorResolver(auth, err);
          // Ask user which second factor to use.
          if (
            resolver.hints[0].factorId === PhoneMultiFactorGenerator.FACTOR_ID
          ) {
            const phoneInfoOptions = {
              multiFactorHint: resolver.hints[0],
              session: resolver.session
            };
            const phoneAuthProvider = new PhoneAuthProvider(auth);
            // Send SMS verification code
            // console.log({ phoneInfoOptions }, { recaptchaVerifier }); //__CONSOLE_LOG__
            localStorage.setItem('pNo', phoneInfoOptions.multiFactorHint.phoneNumber);
            //  this.isLoading = true;
            return phoneAuthProvider
              .verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier)
              .then(async (verificationId) => {
                const dialogRef = this.dialog.open(OtpComponent, {
                  maxWidth: '100vw',
                  maxHeight: '100vh',
                  height: '100%',
                  width: '100%'
                });

                dialogRef.afterOpened().subscribe(() => {
                  // When the dialog is opened, change the z-index to bring it to the front
                  const dialogContainer = this.renderer.selectRootElement('.fade-background');
                  this.renderer.setStyle(dialogContainer, 'z-index', '1');
                });

                dialogRef.afterClosed().subscribe(async result => {
                  //const dialogContainer = this.renderer.selectRootElement('.fade-background');
                  //this.renderer.setStyle(dialogContainer, 'z-index', '9999');
                  let verificationCode = result;
                  const cred = PhoneAuthProvider.credential(
                    verificationId, verificationCode);
                  const multiFactorAssertion =
                    PhoneMultiFactorGenerator.assertion(cred);
                  // Complete sign-in.
                  const userCredential = await resolver.resolveSignIn(multiFactorAssertion);
                  if (!userCredential.user.isAnonymous) {
                    (await this.authService.signIn(userCredential.user, userCredential.user?.reloadUserInfo?.mfaInfo[0]?.phoneInfo)).subscribe(
                      (data: any) => {
                        this.dataService.showTopBar = true;
                        if (localStorage.getItem('eventId')) {
                          if (localStorage.getItem('access') == 'restricted') {
                            this.router.navigateByUrl('/my-photos/' + localStorage.getItem('eventId'));
                          } else {
                            this.router.navigateByUrl('/event-detail/true');
                          }
                          // this.router.navigateByUrl('/event-detail/true');
                        } else {
                          this.router.navigateByUrl("/home");
                        }
                      }
                    )
                    return userCredential
                  }
                  // return userCredential;
                });
              })
          } else {
            // Unsupported second factor.
          }
        }
        return;
      });

    this.confirmRSVP();
    // if (localStorage.getItem('eventId')) {
    //   this.router.navigateByUrl('/event-detail/true');
    // } else {
    //   this.router.navigateByUrl("/home");
    // }
    this.dataService.showTopBar = true;
    if (localStorage.getItem('eventId')) {
      if (localStorage.getItem('access') == 'restricted') {
        this.router.navigateByUrl('/my-photos/' + localStorage.getItem('eventId'));
      } else {
        this.router.navigateByUrl('/event-detail/true');
      }
      // this.router.navigateByUrl('/event-detail/true');
    } else {
      this.router.navigateByUrl("/home");
    }
  }

  async signUpWithApple() {
    this.analytics.capture('login_provider_clicked', { provider: 'apple', surface: 'login_popup' });
    if (this.blockedByStandaloneIOS()) return;
    this.closePopup.emit();
    try {
      const res: any = await this.authService.signUpWithApple();
      // console.log('Apple sign-up success:', res); //__CONSOLE_LOG__

      // Encrypt and store user locally if needed
      if (res.user) {
        this.encryptedData = this.encryptionService.encryptData(JSON.stringify(res.user));
        localStorage.setItem('user', this.encryptedData);
        this.decryptedData = this.encryptionService.decryptData(this.encryptedData);
        // console.log('User data encrypted and stored:', this.decryptedData); //__CONSOLE_LOG__

        // Call backend signIn (no MFA)
        const user = res.user._delegate || res.user;
        const isNewUser = getAdditionalUserInfo(res)?.isNewUser ?? false;
        (await this.authService.signIn(user, null, isNewUser)).subscribe({
          error: () => {
            this.analytics.capture('login_failed', { provider: 'apple', mfa: false, reason: 'backend_signin_failed', surface: 'login_popup' });
          },
          next: async (data: any) => {
          // console.log('Backend signIn response (Apple no MFA) //__CONSOLE_LOG__:', data);
          this.analytics.capture('login_succeeded', { provider: 'apple', mfa: false, is_new_user: isNewUser, surface: 'login_popup' });
          this.dataService.showTopBar = true;

          if (localStorage.getItem('eventId')) {
            this.confirmRSVP();
          } else {
            this.router.navigateByUrl("/home");
          }
          }
        });
      } else {
        // Navigate to home if no MFA required
        this.router.navigateByUrl("/home");
      }

    } catch (err: any) {
      // console.error('Apple login error:', err); //__CONSOLE_LOG__

      if (err.code === "auth/multi-factor-auth-required") {
        // console.log('MFA required for Apple login:', err); //__CONSOLE_LOG__
        this.analytics.capture('login_mfa_required', { provider: 'apple', surface: 'login_popup' });

        // Use existing Firebase app or initialize
        const app = getApps().length ? getApp() : initializeApp(environment.firebase);
        const auth = getAuth(app);
        // console.log('Firebase auth instance for MFA:', auth); //__CONSOLE_LOG__

        // Ensure global recaptcha container exists
        if (!document.getElementById('recaptcha-container')) {
          // console.error('recaptcha-container element not found!'); //__CONSOLE_LOG__
          return;
        }

        // Clear old verifier if any
        if (window.recaptchaVerifier) {
          window.recaptchaVerifier.clear();
        }

        const recaptchaVerifier = new RecaptchaVerifier(
          'recaptcha-container',
          {
            size: 'invisible',
            callback: (response: any) => console.log('reCAPTCHA solved (Apple MFA):', response),
            'expired-callback': () => console.log('reCAPTCHA expired (Apple MFA)')
          },
          auth
        );
        window.recaptchaVerifier = recaptchaVerifier;
        // console.log('RecaptchaVerifier instance (Apple MFA) //__CONSOLE_LOG__:', recaptchaVerifier);

        // Handle multi-factor resolver
        const resolver: any = getMultiFactorResolver(auth, err);
        // console.log('MFA resolver:', resolver); //__CONSOLE_LOG__

        const hint = resolver.hints[0];
        if (hint.factorId === PhoneMultiFactorGenerator.FACTOR_ID) {
          // console.log('Phone MFA selected'); //__CONSOLE_LOG__
          const phoneInfoOptions = { multiFactorHint: hint, session: resolver.session };
          // console.log('Phone info options:', phoneInfoOptions); //__CONSOLE_LOG__

          const phoneAuthProvider = new PhoneAuthProvider(auth);
          localStorage.setItem('pNo', phoneInfoOptions.multiFactorHint.phoneNumber);

          try {
            const verificationId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier);
            // console.log('SMS verificationId:', verificationId); //__CONSOLE_LOG__

            const dialogRef = this.dialog.open(OtpComponent, {
              maxWidth: '100vw',
              maxHeight: '100vh',
              height: '100%',
              width: '100%'
            });

            dialogRef.afterOpened().subscribe(() => {
              // console.log('OTP dialog opened (Apple MFA) //__CONSOLE_LOG__');
              const dialogContainer = this.renderer.selectRootElement('.fade-background');
              this.renderer.setStyle(dialogContainer, 'z-index', '1');
            });

            dialogRef.afterClosed().subscribe(async (verificationCode) => {
              // console.log('OTP dialog closed. Code entered:', verificationCode); //__CONSOLE_LOG__
              if (!verificationCode) {
                this.analytics.capture('login_otp_dismissed', { provider: 'apple', surface: 'login_popup' });
                return;
              }

              const cred = PhoneAuthProvider.credential(verificationId, verificationCode);
              const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
              // console.log('MultiFactor assertion created (Apple MFA) //__CONSOLE_LOG__');

              let userCredential;
              try {
                userCredential = await resolver.resolveSignIn(multiFactorAssertion);
              } catch (otpErr) {
                this.analytics.capture('login_failed', { provider: 'apple', mfa: true, reason: 'invalid_otp', surface: 'login_popup' });
                return;
              }
              const user = userCredential.user;
              // console.log('User signed in with MFA (Apple) //__CONSOLE_LOG__:', user);

              if (!user.isAnonymous) {
                await user.reload();
                const phoneInfo = user.multiFactor.enrolledFactors[0]?.phoneNumber;
                // console.log('User MFA phoneInfo (Apple) //__CONSOLE_LOG__:', phoneInfo);

                (await this.authService.signIn(user, phoneInfo)).subscribe({
                  error: () => {
                    this.analytics.capture('login_failed', { provider: 'apple', mfa: true, reason: 'backend_signin_failed', surface: 'login_popup' });
                  },
                  next: (data: any) => {
                  // console.log('Backend signIn response (Apple MFA) //__CONSOLE_LOG__:', data);
                  this.analytics.capture('login_succeeded', { provider: 'apple', mfa: true, surface: 'login_popup' });
                  this.dataService.showTopBar = true;
                  if (localStorage.getItem('eventId')) {
                    this.confirmRSVP();
                  } else {
                    this.router.navigateByUrl("/home");
                  }
                  }
                });
              }
            });

          } catch (phoneErr) {
            // console.error('Error verifying phone number (Apple MFA) //__CONSOLE_LOG__:', phoneErr);
          }

        } else {
          // console.warn('Unsupported MFA factor (Apple) //__CONSOLE_LOG__:', hint.factorId);
        }
      } else {
        // Previously silent — the user was left on the login screen with no
        // indication anything went wrong. See showGenericSignInError().
        const cancelled = err?.code === 'auth/popup-closed-by-user' || err?.code === 'auth/cancelled-popup-request';
        this.analytics.capture(cancelled ? 'login_dismissed' : 'login_failed', {
          provider: 'apple', surface: 'login_popup',
          ...(cancelled ? {} : { code: err?.code, reason: 'provider_error' }),
        });
        this.showGenericSignInError(err);
      }
    }
  }


  async signUpWithApple1() {
    this.closePopup.emit();
    try {
      await this.authService.signUpWithApple().then((res) => {
        this.confirmRSVP();
        // if (localStorage.getItem('eventId')) {
        //   this.router.navigateByUrl('/event-detail/true');
        // } else {
        //   this.router.navigateByUrl("/home");
        // }
        this.dataService.showTopBar = true;
        if (localStorage.getItem('eventId')) {
          if (localStorage.getItem('access') == 'restricted') {
            this.router.navigateByUrl('/my-photos/' + localStorage.getItem('eventId'));
          } else {
            this.router.navigateByUrl('/event-detail/true');
          }
          // this.router.navigateByUrl('/event-detail/true');
        } else {
          this.router.navigateByUrl("/home");
        }
      })
        .catch((err) => {
        });

    } catch (error) {
    }
  }

  openAppStore() {
    window.open('https://apps.apple.com/in/app/picaggo/id1527686260', '_blank');
  }

  openPlayStore() {
    window.open('https://play.google.com/store/apps/details?id=application.share.photo.picaggo', '_blank');
  }

  async confirmRSVP() {
    const attend_response = localStorage.getItem('attend_response');
    const response = localStorage.getItem('response');
    const invite_type = localStorage.getItem('invite_type');
    const default_upload = localStorage.getItem('default_upload');
    const guestCount = parseInt(localStorage.getItem('guestCount') || '0', 10);
    const access = localStorage.getItem('access') || '';
    (await this.eventService.sendRsvpResponse(response, attend_response, invite_type, default_upload, guestCount, access)).subscribe({
      next: (res: any) => {
        // console.log(res); //__CONSOLE_LOG__ //__CONSOLE_LOG__
        let response: any;
        response = res;
        // Guest has joined the event. Emit the running guest_count when the
        // backend provides it so 1st / 2nd / 10th-guest milestones can be built
        // as PostHog funnel steps filtered on this property. event_id comes
        // from the registered context (set earlier in the invite flow) rather
        // than a manual property here — the previous version sent whichever
        // of localStorage's hex `eventId` or numeric `e` happened to be set,
        // which put a hex value in a field every other event uses numerically.
        this.analytics.capture('guest_joined', {
          guest_count: res?.guest_count ?? res?.result?.guest_count ?? undefined,
        });
        localStorage.removeItem('e');
        localStorage.removeItem('inviteData');
        localStorage.removeItem('eventData');
        localStorage.removeItem('showGuestInvite');
        localStorage.removeItem('redirectUrl');
        localStorage.removeItem('isEventId');
        localStorage.removeItem('openPopup');
        localStorage.removeItem('rsvp');
        localStorage.removeItem('attend_response');
        localStorage.removeItem('response');
        localStorage.removeItem('invite_type');
        localStorage.removeItem('default_upload');
        localStorage.removeItem('guestCount');
        if (localStorage.getItem('access')) {
          this.dataService.prevUserImage = null;
          this.router.navigate(['/my-photos/' + localStorage.getItem('eventId')]);
        } else {
          this.router.navigate(['/event-detail/true']);
        }
        // this.router.navigateByUrl('/event-detail/true');

      },
      error: (err) => {
        // console.error(err); //__CONSOLE_LOG__
      }
    });
  }
}